<template>
  <div v-if="isReportMode">
    <ReportItemContainer>
      <template #question>
        <div v-if="question?.text">
          <TextWithLinks v-if="hasLinks(question.text)" :text="question.text" />
          <div v-else>{{ question.text }}</div>
        </div>
      </template>
      <template #answer>
        <p>{{ boolWithItems.boolean }}</p>
        <p v-for="selectedItem in boolWithItems.selectedItems" :key="selectedItem">{{ selectedItem }}</p>
      </template>
    </ReportItemContainer>
  </div>
  <div v-else>
    <p v-if="question?.text" class="mb-4">
      <TextWithLinks v-if="hasLinks(question.text)" :text="question.text" />
      <div v-else>{{ question.text }}</div>
      <InfoPopup v-if="shouldDisplayInfoIcon" :title="question.helpTitle" :content="question.helpText" />
    </p>
    <BooleanQuestion :answer="boolWithItems.boolean" @q-event="updateBoolean" />
    <div v-if="boolWithItems.boolean === StringBoolean.Yes" class="mt-3">
      <p>{{ question?.detailsText }}</p>
      <PAutocompleteMulti :items="items" :select-items="boolWithItems.selectedItems"
                          @field-updated="updateItems" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import BooleanQuestion from "./BooleanQuestion.vue";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import InfoPopup from "@/components/custom/InfoPopup.vue";
import { StringBoolean } from "@/models/enum/stringBoolean";
import { computed } from "vue";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import { BooleanWithMultiSelect } from "@/models/questions/booleanWithMultiSelect";
import PAutocompleteMulti from "@/components/custom/PAutocompleteMulti.vue";
import { useItemsStore } from "@/stores/itemsStore";
import TextWithLinks from "@/components/questionnaire/TextWithLinks.vue";

const props = withDefaults(defineProps<{
    questionModel: QuestionModelDto,
    answer?: string | object | null,
    isReportMode?: boolean,
  }>(),
  {
    isReportMode: false
  });

const emit = defineEmits<{
  (e: "qEvent", newBoolean: BooleanWithMultiSelect): void
}>();

const itemsStore = useItemsStore();
const Datamap = itemsStore.Datamap;

const boolWithItems = ref(props.answer as BooleanWithMultiSelect ?? new BooleanWithMultiSelect());

const question = computed(() => props.questionModel.question);
const items = computed(() => question.value.dataSource ? Datamap[question.value.dataSource] : new Array<string>());
const shouldDisplayInfoIcon = computed(() => question.value.helpText && question.value.helpText?.length > 0);

function emitNewboolWithItems(): void {
  emit("qEvent", boolWithItems.value);
}

function updateBoolean(boolValue: string): void {
  boolWithItems.value.boolean = boolValue;
  if (boolValue === StringBoolean.No) {
    boolWithItems.value.selectedItems = new Array<string>();
  }
  emitNewboolWithItems();
}

function updateItems(newItems: string[]): void {
  boolWithItems.value.selectedItems = newItems;
  emitNewboolWithItems();
}

function hasLinks(text: string): boolean {
  var regex = new RegExp(/\[(.*?)\]\((.*?)\)/);
  return regex.test(text);
}
</script>

<style lang="scss" scoped>
p {
  white-space: pre-line;
}
</style>
